import { FC, Fragment, useCallback } from 'react';

import { SOURCE_ACCOUNTS_HEADERS, SourceAccountsHeaders } from 'utils/constants/admin';

import { Link, Modal, Table, TableBody, TableCell, TableHeader, TableRow, Text } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { setLoadCampaignAdminAction } from 'store/models/adminCampaignAction';
import { AccountInfo, ApiSourceAccountStatus, setVisibleSourceAccounts } from 'store/models/sourceAccounts';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignAdminActions/SourceAccountsModal/SourceAccountsModal.module.less';

const TrlKeys = {
    title: 'campaign.info.sudo.actions.sourceAccounts',
    close: 'campaign.info.sudo.actions.sourceAccounts.close',
    notFound: 'campaign.info.sudo.actions.sourceAccounts.notFound',
    status: {
        [ApiSourceAccountStatus.ClientNotCreated]: 'campaign.info.sudo.actions.sourceAccounts.status.clientNotCreated',
        [ApiSourceAccountStatus.CampaignNotCreated]:
            'campaign.info.sudo.actions.sourceAccounts.status.campaignNotCreated',
    } as Partial<Record<ApiSourceAccountStatus, string>>,
    headers: {
        [SourceAccountsHeaders.ApiSource]: `campaign.info.sudo.actions.sourceAccounts.${SourceAccountsHeaders.ApiSource}`,
        [SourceAccountsHeaders.ClientId]: `campaign.info.sudo.actions.sourceAccounts.${SourceAccountsHeaders.ClientId}`,
        [SourceAccountsHeaders.DisplayName]: `campaign.info.sudo.actions.sourceAccounts.${SourceAccountsHeaders.DisplayName}`,
        [SourceAccountsHeaders.Link]: `campaign.info.sudo.actions.sourceAccounts.${SourceAccountsHeaders.Link}`,
        [SourceAccountsHeaders.CampaignLink]: `campaign.info.sudo.actions.sourceAccounts.${SourceAccountsHeaders.CampaignLink}`,
    },
};

type AccountSecondHeaders = Exclude<SourceAccountsHeaders, SourceAccountsHeaders.ApiSource>;
const SECOND_HEADERS = SOURCE_ACCOUNTS_HEADERS.slice(1) as AccountSecondHeaders[];

export const SourceAccountsModal: FC = () => {
    const { isVisible, sourceAccountsData = [] } = useSelector((state) => state.sourceAccounts);
    const dispatch = useDispatch();
    const trl = useTrls();

    const onClose = useCallback(
        () => dispatch([setVisibleSourceAccounts(false), setLoadCampaignAdminAction(false)]),
        [dispatch]
    );

    const renderSecondCells = (item: AccountInfo) =>
        SECOND_HEADERS.map((header) => {
            const value = (item as Record<AccountSecondHeaders, string | null>)[header];
            const isLink = [SourceAccountsHeaders.Link, SourceAccountsHeaders.CampaignLink].includes(header);
            return (
                <TableCell key={header}>
                    {isLink && value ? (
                        <Link inline href={value} target="_blank" rel="noopener noreferrer">
                            <Text typography="label-3-regular">
                                <div className={styles.croppedLink}>{value}</div>
                            </Text>
                        </Link>
                    ) : (
                        <Text
                            typography="label-3-regular"
                            style={value ? undefined : 'secondary'}
                            data-qa={value ? undefined : `source-accounts-${header}-not-created`}
                        >
                            {value ?? trl(TrlKeys.status[item.status])}
                        </Text>
                    )}
                </TableCell>
            );
        });

    const content = (
        <Table divider padding={0} cellsGap={32} virtualized={false}>
            <TableHeader>
                <TableRow>
                    {SOURCE_ACCOUNTS_HEADERS.map((header) => (
                        <TableCell key={header} align="left" Element="th">
                            <Text style="secondary" typography="label-3-regular">
                                {trl(TrlKeys.headers[header])}
                            </Text>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {sourceAccountsData.map(({ accountsInfo, apiSource }) => {
                    return (
                        <Fragment key={apiSource}>
                            {!accountsInfo.length ? (
                                <TableRow>
                                    <TableCell>{apiSource}</TableCell>
                                    <TableCell colSpan={SECOND_HEADERS.length}>{trl(TrlKeys.notFound)}</TableCell>
                                </TableRow>
                            ) : (
                                accountsInfo.map((accountInfo, index) => (
                                    <TableRow key={`${accountInfo.clientId ?? accountInfo.displayName}_${index}`}>
                                        {index === 0 ? <TableCell>{apiSource}</TableCell> : <TableCell />}
                                        {renderSecondCells(accountInfo)}
                                    </TableRow>
                                ))
                            )}
                        </Fragment>
                    );
                })}
            </TableBody>
        </Table>
    );

    return (
        <Modal
            visible={isVisible}
            onClose={onClose}
            size="large"
            header={
                <MagritteModalHeader
                    title={trl(TrlKeys.title)}
                    actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onClose} />}
                />
            }
        >
            {content}
        </Modal>
    );
};
